import React, { useMemo, ReactNode } from 'react';
import { useLogger } from '@direct-frontend/modules-logger';
import { getPlatformStyles } from '@direct-frontend/platform-code';

import { TestProps } from '../../internal/types/testTypes';
import { cn } from '../../lib/css/className/className';
import { Image } from '../Image';
import { Header, HeaderProps } from '../Header';
import { Button, ButtonProps } from '../Button';
import { VStack } from '../Stack';
import { Modal, ModalProps } from '../Modal';
import { useStack } from '../../hooks/useStack';
import { TextGeneric } from '../TextGeneric/TextGeneric';
import { LayerPriorities } from '../../lib/layerManager/layerManager';

import { i18n } from './ModalConfirm.i18n';

getPlatformStyles({
    common: import('./ModalConfirm.css'),
    mobile: import('./ModalConfirm@mobile.css'),
});

// В модалке обязательно должен быть либо headerText, либо bodyText
export type ModalConfirmBodyProps =
    | {
          /**
           * Текст в заголовке
           */
          headerText: ReactNode;
          /**
           * Поясняющий текст
           */
          bodyText?: ReactNode;
      }
    | {
          /**
           * Текст в заголовке
           */
          headerText?: ReactNode;
          /**
           * Поясняющий текст
           */
          bodyText: ReactNode;
      };

type ButtonParams<Color extends ButtonProps['color']> = Pick<
    ButtonProps,
    'onClick' | 'href' | 'target' | 'routerOptions' | 'data-testid' | 'isDisabled' | 'isLoading' | 'logNode'
> & {
    /**
     * Текст внутри кнопки
     */
    text?: string;

    /**
     * Цвет кнопки
     */
    color: Color;
};

type AcceptButton = ButtonParams<'accent' | 'normal'>;
type CancelButton = ButtonParams<'normal' | 'text-supplementary'>;

type ButtonsSetting = {
    /**
     * Настройка кнопок внутри модального окна. Ожидаем либо одну кнопку серого или желтого цвета,
     * либо две/три кнопки, где первая желтая, а вторая/третья серая или прозрачная
     */
    buttons: [AcceptButton] | [AcceptButton, CancelButton] | [AcceptButton, CancelButton, CancelButton | undefined];
    /**
     * Размер кнопок в модальном окне
     * @default l
     * @deprecated
     */
    buttonsSize?: ButtonProps['size'];
};

/** Пропсы модального окна для показа подтверджения */
export type ModalConfirmProps = TestProps &
    Pick<
        ModalProps,
        | 'isOpen'
        | 'onClose'
        | 'portalContainer'
        | 'priority'
        | 'dismissOnOutsideClick'
        | 'logNode'
        | 'scrollable'
        | 'preventScroll'
        | 'onAnimationEnd'
    > &
    ModalConfirmBodyProps &
    ButtonsSetting & {
        /**
         * Выравнивание текста в заголовке и тексте
         * @default center
         */
        textAlign?: HeaderProps['textAlign'];
        /**
         * URL изображения
         */
        imageSrc?: string;

        /**
         * Расположение кнопок в модальном окне
         * @default vertical
         */
        buttonsLayout?: 'horizontal' | 'vertical';
        /**
         * Тип изображения в модальном окне:
         * illustration — маленькая иллюстрация, image — картинка на всю ширину модалки
         * @default illustration
         */
        type?: 'illustration' | 'image';
        /**
         * Дополнительный класс для контента модалки
         */
        className?: string;
    };

const cnModalConfirm = cn('ModalConfirm');

/**
 * Модальное окно для показа подтверждений.
 */
export function ModalConfirm(props: ModalConfirmProps) {
    const {
        isOpen = false,
        headerText,
        textAlign = 'center',
        bodyText,
        imageSrc,
        priority = LayerPriorities.Default,
        buttons,
        buttonsSize = 'l',
        onClose,
        onAnimationEnd,
        portalContainer,
        logNode,
        className,
        buttonsLayout = 'vertical',
        type = 'illustration',
        ['data-testid']: dataTestId = 'ModalConfirm',
        dismissOnOutsideClick = true,
        scrollable = false,
        preventScroll,
    } = props;

    const { LogProvider } = useLogger('ModalConfirm');
    const [firstButton, secondButton, thirdButton] = buttons;
    const stackClass = useStack({ type: buttonsLayout, gap: '12' });
    const imageSize = type === 'illustration' ? 120 : '100%';
    const content = useMemo(() => {
        const header = headerText ? (
            <Header level="h2" textAlign={textAlign}>
                {headerText}
            </Header>
        ) : null;

        const body = bodyText ? (
            <TextGeneric textSize="16-24" color="gray" role={'supplementary'} textAlign={textAlign}>
                {bodyText}
            </TextGeneric>
        ) : null;

        if (header && body) {
            return (
                <VStack gap="6">
                    {header}
                    {body}
                </VStack>
            );
        }

        return header || body;
    }, [headerText, textAlign, bodyText]);

    return (
        <LogProvider>
            <Modal
                isOpen={isOpen}
                className={cnModalConfirm(null, [className])}
                containerClassName={cnModalConfirm('container')}
                hasCloseButton={Boolean(onClose)}
                onClose={onClose}
                data-testid={dataTestId}
                priority={priority}
                scrollable={scrollable}
                position={'center'}
                portalContainer={portalContainer}
                dismissOnOutsideClick={dismissOnOutsideClick}
                preventScroll={preventScroll}
                logNode={logNode}
                onAnimationEnd={onAnimationEnd}
            >
                {Boolean(imageSrc) && (
                    <Image
                        className={cnModalConfirm('image', { type })}
                        src={imageSrc}
                        alt=""
                        aria-hidden
                        width={imageSize}
                        height={imageSize}
                    />
                )}
                <VStack gap="28">
                    {content}
                    <div className={stackClass}>
                        <Button
                            {...firstButton}
                            size={buttonsSize}
                            width="max"
                            data-testid={firstButton['data-testid'] ?? `${dataTestId}.Confirm`}
                        >
                            {firstButton.text ?? i18n('Да')}
                        </Button>
                        {secondButton && (
                            <Button
                                {...secondButton}
                                size={buttonsSize}
                                width="max"
                                data-testid={secondButton['data-testid'] ?? `${dataTestId}.Cancel`}
                            >
                                {secondButton.text ?? i18n('Нет')}
                            </Button>
                        )}
                        {thirdButton && (
                            <Button
                                {...thirdButton}
                                size={buttonsSize}
                                width="max"
                                data-testid={thirdButton['data-testid'] ?? `${dataTestId}.Additional`}
                            >
                                {thirdButton.text}
                            </Button>
                        )}
                    </div>
                </VStack>
            </Modal>
        </LogProvider>
    );
}
